import React, { useState, useEffect, useRef, useMemo } from 'react'
import { useTranslation } from 'next-i18next'

const parse = (str) => {
  const m = (str || "").match(/^[\d.,\s]*/);
  const num = parseInt((m?.[0] || "").replace(/[.,\s]/g, ""), 10) || 0;
  return { num, suffix: (str || "").slice((m?.[0] || "").length).trim() };
};

const OurAchievement = () => {
  const { t } = useTranslation()
  const ref = useRef(null)
  const [inView, setInView] = useState(false)
  const [counts, setCounts] = useState([0, 0, 0, 0])

  const achievements = useMemo(() => [
    { ...parse(t("home-page.statistics-stat-1-number")), description: t("home-page.statistics-stat-1-description") },
    { ...parse(t("home-page.statistics-stat-2-number")), description: t("home-page.statistics-stat-2-description") },
    { ...parse(t("home-page.statistics-stat-3-number")), description: t("home-page.statistics-stat-3-description") },
    { ...parse(t("home-page.statistics-stat-4-number")), description: t("home-page.statistics-stat-4-description") },
  ], [t])

  useEffect(() => {
    if (!ref.current) return
    const obs = new IntersectionObserver(([e]) => e.isIntersecting && setInView(true), { threshold: 0.2 })
    obs.observe(ref.current)
    return () => obs.disconnect()
  }, [])

  useEffect(() => {
    if (!inView) return
    const targets = achievements.map((a) => a.num)
    const start = Date.now()
    const id = setInterval(() => {
      const p = Math.min((Date.now() - start) / 2000, 1)
      const ease = 1 - (1 - p) ** 4
      setCounts(targets.map((n) => Math.floor(ease * n)))
      if (p >= 1) clearInterval(id)
    }, 16)
    return () => clearInterval(id)
  }, [inView, achievements])

  return (
    <section
      ref={ref}
      className="relative py-[80px] md:py-[200px] bg-gradient-to-b from-[#091F59] to-[#1A3884] mt-[60px] md:mt-[0px] mb-[100px] md:mb-[200px] overflow-hidden"
    >
      <div className="relative max-w-[90%] mx-auto z-10 flex flex-col items-center">
        <div className="flex flex-col items-center text-center mb-[60px]">
          <h2 className="text-[32px] md:text-[48px] lg:text-[64px] xl:text-[80px] font-[500] text-white leading-tight mb-[16px] md:mb-[32px]">
            {t("about-us-page.our-achievement-title")}
          </h2>
          <p className="text-white/80 text-[14px] md:text-[16px] lg:text-[20px] font-[400] max-w-2xl leading-relaxed">
            {t("about-us-page.our-achievement-description")}
          </p>
        </div>
        <div className="bg-white rounded-[32px] md:rounded-[48px] p-[40px] shadow-lg w-full xl:w-fit">
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 items-start justify-center gap-y-[40px] md:gap-y-[50px] lg:gap-[50px] xl:gap-[70px]">
            {achievements.map((achievement, index) => (
              <div
                key={index}
                className="flex flex-col items-center text-center max-w-[290px] w-full mx-auto xl:mx-0 xl:max-w-none"
              >
                <div className="min-h-[48px] flex items-center justify-center text-[32px] font-[600] text-main-pink mb-[12px] md:mb-[16px]">
                  {(counts[index] ?? 0).toLocaleString()}{achievement.suffix}
                </div>
                <p className="text-[16px] font-[400] text-gray-700 leading-relaxed">
                  {achievement.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

export default OurAchievement
